import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const PRODUCTION_SHOT =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_site-creator-3339/artifacts/jvpwvyna_mrv-production-monitoring-580C-2640w.webp";

const APPLICATION_SHOT =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_site-creator-3339/artifacts/nrh7d04f_mrv-application-tracking-2640w.webp";

const CAPABILITIES = [
  "Feedstock Traceability",
  "Live Production Monitoring",
  "Batch & Product Traceability",
  "Inventory & Dispatch",
  "Application Evidence",
  "Carbon Accounting",
  "Verification Ready",
  "Audit Trail",
];

const Mrv = () => {
  usePageMeta(
    "MRV — Convergent, Tanren's Carbon MRV Platform | Tanren",
    "Convergent connects operational data, material traceability and carbon evidence — from feedstock intake to verified carbon removal."
  );

  return (
    <div data-testid="page-mrv">
      {/* HERO */}
      <section
        data-testid="mrv-hero"
        className="bg-paper text-char-900 pt-40 pb-16 lg:pb-20 border-b border-char-900/10"
      >
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-y-8 md:gap-x-16 items-end">
          <h1 className="md:col-span-7 font-display text-char-900">
            MRV built around the way the plant actually runs.
          </h1>
          <p className="md:col-span-5 text-lg md:text-xl text-char-700 leading-relaxed">
            Convergent connects operational data, material traceability and carbon evidence — from
            feedstock intake to verified carbon removal.
          </p>
        </div>
      </section>

      {/* PLATFORM VISUAL 01 — production monitoring */}
      <section data-testid="mrv-production-visual" className="bg-paper py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <ScrollReveal>
            <figure className="m-0">
              <div className="border border-char-900/10 overflow-x-auto rounded-[22px]">
                <img
                  src={PRODUCTION_SHOT}
                  alt="Convergent production monitoring — reactor temperature over time"
                  data-testid="mrv-shot-production"
                  width="2640"
                  height="1178"
                  className="block w-full min-w-[720px] md:min-w-0 h-auto"
                  loading="lazy"
                />
              </div>
              <figcaption className="mono-label mt-5">
                Fig. 01 · Production monitoring — live reactor data from plant PLC / SCADA
              </figcaption>
            </figure>
          </ScrollReveal>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section data-testid="mrv-capabilities" className="bg-paper py-16 lg:py-24 border-t border-char-900/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <ScrollReveal>
            <h2 className="font-display text-char-900 max-w-[24ch]">
              One record, end to end.
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid md:grid-cols-2 md:gap-x-16 border-t border-char-900/10">
            {CAPABILITIES.map((c, i) => (
              <ScrollReveal key={c} delay={i * 40}>
                <div
                  data-testid={`mrv-capability-${i + 1}`}
                  className="flex items-baseline gap-6 py-5 border-b border-char-900/10"
                >
                  <span className="numeral text-xl w-8 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-base tracking-tight text-char-900">{c}</span>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM NOTE */}
      <section data-testid="mrv-platform-note" className="bg-paper py-16 lg:py-20 border-t border-char-900/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-12 md:gap-x-16 gap-y-8">
          <ScrollReveal className="md:col-span-5">
            <div>
              <span className="mono-label">The platform</span>
            </div>
            <h2 className="font-display text-char-900 mt-4">
              Convergent — our MRV platform
            </h2>
          </ScrollReveal>
          <ScrollReveal className="md:col-span-7">
            <p className="text-lg text-char-700 leading-relaxed">
              Convergent is Tanren's monitoring, reporting, and verification platform for carbon
              removal projects — starting with biochar. Carbon standards are modeled as versioned,
              swappable configs rather than hardcoded logic, so the platform extends cleanly as new
              pathways and methodologies come online.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* PLATFORM VISUAL 02 — application tracking */}
      <section data-testid="mrv-application-visual" className="bg-paper py-16 lg:py-20 border-t border-char-900/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <ScrollReveal>
            <figure className="m-0">
              <div className="border border-char-900/10 overflow-x-auto rounded-[22px]">
                <img
                  src={APPLICATION_SHOT}
                  alt="Convergent application tracking — geotagged records of biochar end use"
                  data-testid="mrv-shot-application"
                  width="2640"
                  height="1445"
                  className="block w-full min-w-[720px] md:min-w-0 h-auto"
                  loading="lazy"
                />
              </div>
              <figcaption className="mono-label mt-5">
                Fig. 02 · Application tracking — geotagged, photo-evidenced records of end use
              </figcaption>
            </figure>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="mrv-cta-section" className="bg-char-900 text-paper py-20 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div className="max-w-[62ch]">
            <h2 className="font-display max-w-[28ch]">Rigor, tonne by tonne.</h2>
            <p className="mt-6 text-lg leading-relaxed">
              Built for biochar producers, carbon project developers and buyers who need every tonne
              of carbon removal backed by plant data, traceable material and audit-ready evidence.
              Carbon standards are configured as versioned modules, so new methodologies can be
              added as they come online.
            </p>
          </div>
          <Link to="/contact" data-testid="mrv-cta" className="btn-ember shrink-0">
            Book a Convergent demo
            <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Mrv;
